import React, { useState } from 'react'
import { Check, Eye, EyeOff, Lock, X } from 'lucide-react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@/components/ui/button'
import { Form, FormControl, FormField, FormItem, FormMessage, FormLabel } from '@/components/ui/form'
import { CustomDialog } from '@/components/dialogs/custom-dialog'
import { passwordChangeSchema, PasswordChangeSchema } from '@/entities/user'
import { useAuthStore } from '@/store/auth'
import { showConfirm } from '@/lib/alerts'
import { api } from '@/lib/axios'
import { useToast } from '@/hooks/use-toast'
import { HTTP_STATUS_CODE } from '@/constants/http'
import { useLoader } from '@/contexts/LoaderContext'
import { Input } from '@/components/ui_custom/input'

// types
type PasswordChangeDialogProps = {
  open: boolean
  cb: () => void
}

// variables
const loc = 'components/dialogs/password-change-dialog'

export function PasswordChangeDialog({ open, cb }: PasswordChangeDialogProps) {
  const { user, logout } = useAuthStore()
  const { toast } = useToast()
  const { showLoader, hideLoader, isLoading } = useLoader()
  const [isShowingPassword, setIsShowingPassword] = useState(false)
  const [isShowingNewPassword, setIsShowingNewPassword] = useState(false)
  const [isShowingConfirmPassword, setIsShowingConfirmPassword] = useState(false)

  const form = useForm<PasswordChangeSchema>({
    resolver: zodResolver(passwordChangeSchema),
    defaultValues: {
      userRef: user?.ref,
      password: '',
      newPassword: '',
      confirmPassword: ''
    }
  })

  const formValues = useWatch({ control: form.control })

  const _handleChangePassword = async () => {
    try {
      const isValid = await form.trigger()
      if (!isValid) return

      const { isConfirmed } = await showConfirm('Deseja confirmar?')
      if (!isConfirmed) return

      showLoader()

      const response = await api.put('/', {
        ...formValues,
        action: 'password',
        userRef: user?.ref
      })

      if ([HTTP_STATUS_CODE.OK].includes(response.data?.statusCode)) {
        toast({
          variant: 'success',
          title: 'Sucesso',
          description: response.data?.statusMessage || `Senha alterada com sucesso.`
        })
        cb()
        logout()
      } else toast({ variant: 'destructive', title: 'Ops ...', description: response.data?.statusMessage || 'Não foi possível alterar a senha.' })
    } catch (err) {
      toast({ variant: 'destructive', title: 'Ops ...', description: 'Não foi possível alterar a senha.' })
      console.error(`Unhandled error at ${loc}._handleChangePassword. Details: ${err}`)
    } finally {
      hideLoader()
    }
  }

  const _handleCancel = async () => {
    const { isConfirmed } = await showConfirm('Deseja cancelar?')
    if (!isConfirmed) return
    cb()
  }

  return (
    <CustomDialog
      open={open}
      persistent={true}
      title={`Alterar senha`}
      confirmationBeforeClose={true}
      onClose={() => {
        cb()
      }}
    >
      <Form {...form}>
        <form
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault()
              _handleChangePassword()
            }
          }}
        >
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem className="w-full mb-5">
                <FormLabel>Senha atual: *</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Digite a senha atual"
                    disabled={isLoading}
                    type={isShowingPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    leftIcon={<Lock className="h-5 w-5" />}
                    rightIcon={isShowingPassword ? <EyeOff className="w-5 h-5 hover:cursor-pointer z-[100]" onClick={() => setIsShowingPassword(prev => !prev)} /> : <Eye className="w-5 h-5 hover:cursor-pointer z-[100]" onClick={() => setIsShowingPassword(prev => !prev)} />}
                  />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="newPassword"
            render={({ field }) => (
              <FormItem className="w-full mb-5">
                <FormLabel>Nova senha: *</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Digite a nova senha"
                    disabled={isLoading}
                    type={isShowingNewPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    leftIcon={<Lock className="h-5 w-5" />}
                    rightIcon={isShowingNewPassword ? <EyeOff className="w-5 h-5 hover:cursor-pointer z-[100]" onClick={() => setIsShowingNewPassword(prev => !prev)} /> : <Eye className="w-5 h-5 hover:cursor-pointer z-[100]" onClick={() => setIsShowingNewPassword(prev => !prev)} />}
                  />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem className="w-auto mb-5">
                <FormLabel>Confirmar senha: *</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Digite a confirmação de senha"
                    disabled={isLoading}
                    type={isShowingConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    leftIcon={<Lock className="h-5 w-5" />}
                    rightIcon={isShowingConfirmPassword ? <EyeOff className="w-5 h-5 hover:cursor-pointer z-[100]" onClick={() => setIsShowingConfirmPassword(prev => !prev)} /> : <Eye className="w-5 h-5 hover:cursor-pointer z-[100]" onClick={() => setIsShowingConfirmPassword(prev => !prev)} />}
                  />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />

          <div className="col-span-2 mt-4 pt-4 border-t flex items-center gap-x-2 justify-end">
            <Button variant="default" type="button" className="flex gap-x-2" onClick={_handleChangePassword}>
              Alterar senha
              <Check className="size-5" />
            </Button>

            <Button variant="outline" type="button" className="flex gap-x-2 border-none" onClick={_handleCancel}>
              Cancelar
              <X className="size-4" />
            </Button>
          </div>
        </form>
      </Form>
    </CustomDialog>
  )
}
